import Servers from '../servers';

/**
 * @module cercaliagl/layer/XYZ
 */

/**
 * @typedef {Object} Options
 * @property {string} layerId Layer id.
 * @property {string} [label] Layer label. If not specified it uses `layerId` value. This value is visible in {@link module:cercaliagl/control/LayerSwitcher~LayerSwitcher}.
 * @property {string} [attribution] Attribution HTML for this layer. This value is visible in {@link module:cercaliagl/control/Logo~Logo}.
 * @property {string|Array.<string>} url URL or an array of urls templates. Must include `{x}`, `{y}` and `{z}`.
 * @property {number} tilesize Tile size in pixels. Default `256`.
 * @property {number} minzoom Minimum zoom level. Default `0`.
 * @property {number} maxzoom Maximum zoom level. Default `20`.
 */

/**
 * @classdesc
 * Raster base layer. Useful for custom baselayers.
 *
 * Example usage:
 *
 * ```
 * const map = new cercaliagl.Map({
 *   target: 'map',
 *   center: new cercaliagl.LonLat(2.16935, 41.4046),
 *   zoom: 12,
 * });
 *
 * map.whenReady(() => {
 *   const iccTopoLayer = new cercaliagl.layer.XYZ({
 *     layerId: 'icc',
 *     label: 'ICC Topogràfic',
 *     url: 'https://geoserveis.icgc.cat/icc_mapesmultibase/noutm/wmts/topo/GRID3857/{z}/{x}/{y}.jpeg',
 *     tileSize: 256,
 *   });

 *   const iccOrtoLayer = new cercaliagl.layer.XYZ({
 *     layerId: 'icc_orto',
 *     label: 'ICC Aerea',
 *     url: 'https://geoserveis.icgc.cat/icc_mapesmultibase/noutm/wmts/orto/GRID3857/{z}/{x}/{y}.jpeg',
 *     tileSize: 256,
 *   });
 *
 *   map.addBaseLayer(iccTopoLayer);
 *   map.addBaseLayer(iccOrtoLayer);
 * });
 *
 * ```
 *
 * @api
 */
class XYZ {
  /**
   * @constructor
   * @param {Options} options XYZ options
   * @api
   */
  constructor(options) {
    /**
     * @private
     * @type {string}
     */
    this.layerId_ = options.layerId;

    /**
     * @private
     * @type {string}
     */
    this.label_ = options.label || this.layerId_;

    /**
     * @private
     * @type {string}
     */
    this.attribution_ = options.attribution || '';

    /**
     * @private
     * @type {maplibregl.Style}
     */
    this.style_ = {
      version: 8,
      attribution: this.attribution_,
      glyphs: Servers.glyphs,
      sources: {
        [this.layerId_]: {
          type: 'raster',
          scheme: options.scheme ? options.scheme : 'xyz',
          tiles: Array.isArray(options.url) ? options.url : [options.url],
          tileSize: options.tilesize || 256,
          minzoom: options.minzoom || 0,
          maxzoom: options.maxzoom || 20,
          crossOrigin: 'anonymous',
        },
        buildings3d: {
          type: 'vector',
          tiles: [Servers.buildings3d],
          minzoom: 15,
          maxzoom: options.maxzoom || 20,
        },
        '3dlandmarks': {
          type: 'vector',
          tiles: [Servers.landmarks3d],
          minzoom: 12,
          maxzoom: options.maxzoom || 20,
        },
      },
      layers: [
        {
          id: this.layerId_,
          source: this.layerId_,
          type: 'raster',
          minzoom: 2,
          maxzoom: options.maxzoom || 20,
        },
        {
          id: 'buildings3d',
          type: 'fill-extrusion',
          source: 'buildings3d',
          'source-layer': 'building',
          minzoom: 15,
          layout: { visibility: 'none' },
          paint: {
            'fill-extrusion-color': 'rgba(187, 187, 187, 1)',
            'fill-extrusion-opacity': 0.6,
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': ['get', 'min_height'],
          },
        },
        {
          id: '3dlandmarks',
          type: 'fill',
          source: '3dlandmarks',
          'source-layer': '3dlandmarks',
          minzoom: 15,
          paint: {
            'fill-color': '#088',
            'fill-opacity': 0,
          },
          layout: {
            visibility: 'none',
          },
        },
      ],
    };
  }

  /**
   * Get layer id
   * @return {string} Layer id
   * @api
   */
  getLayerId() {
    return this.layerId_;
  }

  /**
   * Map label used in layer switcher.
   * @return {string} Name
   * @api
   */
  getLabel() {
    return this.label_;
  }

  /**
   * Get attributon
   * @return {string} Attribution
   * @api
   */
  getAttribution() {
    return this.attribution_;
  }

  /**
   * @return {maplibregl.Style}
   */
  getMapboxStyle() {
    return this.style_;
  }
}

export default XYZ;
